import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Paper, IconButton, Tooltip, Chip, Tabs, Tab, Badge, Avatar, CardActionArea, Drawer, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { IoApps } from 'react-icons/io5';
import { FaTruck, FaPalette, FaServer, FaGavel, FaFlask, FaBullhorn, FaBoxes, FaInfo, FaMagic, FaClock, FaDollarSign, FaShareAlt, FaCheckDouble, FaEnvelopeOpenText, FaBarcode } from 'react-icons/fa';
import { MdOutlineTranslate } from 'react-icons/md';
import { useFavorites } from '../../context/FavoritesContext';
import { usePermissions } from '../../context/PermissionContext';
import { TOOL_COLORS } from '../../constants/colors';

const RECENT_VISITS_KEY = 'recentPageVisits';

const OthersLanding = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { favorites, toggleFavorite, isFavorite } = useFavorites();
  const { isAdmin, isOwner, hasAccessToPath } = usePermissions();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedTool, setSelectedTool] = useState(null);
  const [lastUsedMap, setLastUsedMap] = useState({});

  // Load last used times from localStorage
  useEffect(() => {
    const loadLastUsedTimes = () => {
      try {
        const stored = localStorage.getItem(RECENT_VISITS_KEY);
        if (stored) {
          const visits = JSON.parse(stored);
          const timeMap = {};
          visits.forEach(visit => {
            if (!timeMap[visit.path]) {
              timeMap[visit.path] = new Date(visit.timestamp);
            }
          });
          setLastUsedMap(timeMap);
        }
      } catch (err) {
        console.error('Error loading last used times:', err);
      }
    };
    loadLastUsedTimes();
  }, []);

  // Format time ago from date
  const formatTimeAgo = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / 60000);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''} ago`;
  };

  // Get last used time for a path
  const getLastUsed = (path) => {
    return formatTimeAgo(lastUsedMap[path]);
  };

  const otherInterfaces = [
    {
      name: '48 State Freight',
      path: '/dashboard/48-state-freight',
      description: 'Calculate freight costs for 48 states based on item dimensions, weight, and FedEx Ground shipping constraints with surcharge calculations.',
      icon: FaTruck,
      category: 'Logistics',
      isNew: false,
    },
    {
      name: 'Customer Style Search',
      path: '/dashboard/customer-style-search',
      description: 'Advanced search capabilities allowing customers to find products based on style preferences and visual similarity.',
      icon: FaPalette,
      category: 'Customer Tools',
      isNew: false,
    },
    {
      name: 'Document Translator',
      path: '/dashboard/document-translation',
      description: 'Multi-language document translation service supporting over 100 languages with industry-specific terminology.',
      icon: MdOutlineTranslate,
      category: 'Language Tools',
      isNew: false,
    },
    // FTP Transfer - DISABLED - Interface hidden
    // {
    //   name: 'FTP Transfer',
    //   path: '/dashboard/ftp-transfer',
    //   description: 'Download and organize AR ZIP files from SFTP server. Automatically sorts files into 2D/3D folders based on TCIN mapping from Excel.',
    //   icon: FaServer,
    //   category: 'File Management',
    //   isNew: false,
    // },
    // Furniture Regulations - DISABLED - Interface hidden
    // {
    //   name: 'Furniture Regulations',
    //   path: '/dashboard/furniture-regulations',
    //   description: 'Comprehensive database of furniture safety regulations, compliance requirements, and certification processes.',
    //   icon: FaGavel,
    //   category: 'Compliance',
    //   isNew: false,
    // },
    // {
    //   name: 'Furniture Testing',
    //   path: '/dashboard/furniture-testing',
    //   description: 'Digital interface for tracking furniture testing procedures, certification status, and quality assurance metrics.',
    //   icon: FaFlask,
    //   category: 'Quality Control',
    //   isNew: false,
    // }, // DISABLED - Interface hidden
    {
      name: 'Marketing Copies',
      path: '/dashboard/marketing-copies',
      description: 'AI-enhanced marketing copy creation tool with SEO optimization and brand voice consistency checking.',
      icon: FaBullhorn,
      category: 'Marketing',
      isNew: false,
    },
    // Pallet Configuration - DISABLED - Interface hidden
    // {
    //   name: 'Pallet Configuration',
    //   path: '/dashboard/pallet-configuration',
    //   description: 'Optimize pallet loading configurations for maximum space efficiency and shipping cost reduction.',
    //   icon: FaBoxes,
    //   category: 'Logistics',
    //   isNew: false,
    // },
    {
      name: 'Price Scraper',
      path: '/dashboard/price-scraper',
      description: 'Scrape product prices from Amazon, Walmart, and Bed Bath & Beyond. Import SKUs via Excel and compare prices across retailers.',
      icon: FaDollarSign,
      category: 'Price Comparison',
      isNew: false,
    },
    {
      name: 'Product Details',
      path: '/dashboard/product-details',
      description: 'Centralized product information management system with automated data synchronization and validation.',
      icon: FaInfo,
      category: 'Product Management',
      isNew: false,
    },
    {
      name: 'Remove Background',
      path: '/dashboard/remove-background',
      description: 'Professional-grade image background removal tool powered by machine learning for perfect product photos.',
      icon: FaMagic,
      category: 'Image Processing',
      isNew: false,
    },
    {
      name: 'Social Media Content',
      path: '/dashboard/social-media-content',
      description: 'Generate platform-specific captions for Instagram, Facebook, Pinterest, and TikTok from product data and images.',
      icon: FaShareAlt,
      category: 'Marketing',
      isNew: false,
    },
    {
      name: 'Product Data Validator',
      path: '/dashboard/product-data-validator',
      description: 'Validate product feeds against rules: required fields, image URLs, UPC checksums, dimensions, banned words.',
      icon: FaCheckDouble,
      category: 'Quality Control',
      isNew: false,
    },
    {
      name: 'Email Campaign Builder',
      path: '/dashboard/email-campaign-builder',
      description: 'Generate full email marketing copy with A/B subject lines, hero sections, and CTAs for campaigns.',
      icon: FaEnvelopeOpenText,
      category: 'Marketing',
      isNew: false,
    },
    {
      name: 'Barcode / QR Generator',
      path: '/dashboard/barcode-generator',
      description: 'Generate barcodes (Code 128, UPC, EAN) and QR codes from item IDs. Single or batch mode.',
      icon: FaBarcode,
      category: 'Operations',
      isNew: false,
    },
    {
      name: 'File Converter',
      path: '/dashboard/file-converter',
      description: 'Drag & drop any file to convert: PNG↔JPG, Excel↔CSV, HEIC→PNG, BMP↔GIF, and more. Instant conversion.',
      icon: FaMagic,
      category: 'Operations',
      isNew: false,
    },
    {
      name: 'Reports Scheduler',
      path: '/dashboard/reports-scheduler',
      description: 'Run warehouse and product reports on demand or on a cron timetable, with run history and downloads. Administrator and Owner only.',
      icon: FaTruck,
      category: 'Automation',
      status: 'active',
      isNew: true,
    },
    {
      name: 'Pallet Optimizer',
      path: '/dashboard/pallet-optimizer',
      description: 'Pack each PO onto the fewest shipping pallets using item dimensions from Items Detail and a fixed capacity table; infers missing dims from same-design/size siblings and flags the rest.',
      icon: FaTruck,
      category: 'Logistics',
      isNew: true,
    }
  ];

  const categories = ['All', 'Logistics', 'Customer Tools', 'Language Tools', 'Infrastructure', 'Compliance', 'Quality Control', 'Marketing', 'Price Comparison', 'Product Management', 'Image Processing', 'Operations'];

  // Filter interfaces based on user permissions
  const permittedInterfaces = useMemo(() => {
    if (isAdmin || isOwner) {
      return otherInterfaces;
    }
    return otherInterfaces.filter(item => {
      // Extract path without /dashboard/ prefix for permission check
      const path = item.path.replace('/dashboard/', '');
      return hasAccessToPath(path);
    });
  }, [isAdmin, isOwner, hasAccessToPath]);

  const filteredTools = selectedTab === 0
    ? permittedInterfaces
    : permittedInterfaces.filter(tool => tool.category === categories[selectedTab]);

  const sidebarContent = () => (
    <Paper
      elevation={0}
      sx={{
        width: 280,
        height: '100%',
        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'white',
        borderRight: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Sidebar Header */}
      <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: TOOL_COLORS.OTHER.primary,
            }}
          >
            <IoApps size={28} color="white" />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Other Tools
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {permittedInterfaces.length} tools available
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Category Tabs */}
      <Tabs
        orientation="vertical"
        value={selectedTab}
        onChange={(e, newValue) => { setSelectedTab(newValue); if (isMobile) setSidebarOpen(false); }}
        sx={{
          borderRight: 0,
          '& .MuiTabs-indicator': {
            left: 0,
            width: 4,
            bgcolor: TOOL_COLORS.OTHER.primary,
          },
        }}
      >
        {categories.map((category, index) => (
          <Tab
            key={category}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                <Typography variant="body2">{category}</Typography>
                {index === 0 && (
                  <Chip label={permittedInterfaces.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                )}
                {index > 0 && (
                  <Chip
                    label={permittedInterfaces.filter(t => t.category === category).length}
                    size="small"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            }
            sx={{
              textAlign: 'left',
              alignItems: 'flex-start',
              px: 3,
              py: 1.5,
              minHeight: 48,
              textTransform: 'none',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.2s ease',
              '&.Mui-selected': {
                bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
              },
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 140, 0, 0.08)' : 'rgba(255, 140, 0, 0.04)',
                transform: 'translateX(4px)',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                width: '0%',
                height: '3px',
                borderRadius: '2px',
                transform: 'translateX(-50%)',
                transition: 'width 0.3s ease-in-out',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 10' preserveAspectRatio='none'%3E%3Cpath d='M0 5 Q25 0 50 5 T100 5' fill='none' stroke='%23FF8C00' stroke-width='4'/%3E%3C/svg%3E")`,
                backgroundSize: '100% 100%',
              },
              '&:hover::after': {
                width: '80%',
              },
            }}
          />
        ))}
      </Tabs>

      {/* Quick Stats */}
      <Box sx={{ mt: 'auto', p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
          Quick Stats
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">Recently Used</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {permittedInterfaces.filter(t => lastUsedMap[t.path]).length}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">Favorites</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#FFD700' }}>
              {favorites.length}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">New Tools</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#FF6B6B' }}>
              {permittedInterfaces.filter(t => t.isNew).length}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: theme.palette.background.default }}>
      {/* Sidebar */}
      {isMobile ? (
        <Drawer anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)} PaperProps={{ sx: { width: 280 } }}>
          {sidebarContent()}
        </Drawer>
      ) : (
        sidebarContent()
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'white',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isMobile && (
              <IconButton onClick={() => setSidebarOpen(true)} sx={{ mr: 1 }}>
                <MenuIcon />
              </IconButton>
            )}
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                {categories[selectedTab]} Tools
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                Select a tool from our specialized collection
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Tools Grid */}
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(350px, 1fr))' }, gap: 2 }}>
            {filteredTools.map((item) => {
              const IconComponent = item.icon;
              const isFav = isFavorite(item.path);
              const isSelected = selectedTool === item.path;
              
              return (
                <Paper
                  key={item.path}
                  elevation={0}
                  sx={{
                    border: `2px solid ${isSelected ? TOOL_COLORS.OTHER.primary : theme.palette.divider}`,
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: isSelected
                      ? theme.palette.mode === 'dark' ? TOOL_COLORS.OTHER.light : `${TOOL_COLORS.OTHER.primary}05`
                      : 'transparent',
                    '&:hover': {
                      borderColor: TOOL_COLORS.OTHER.primary,
                      transform: 'translateX(4px)',
                      boxShadow: theme.shadows[2],
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      width: '0%',
                      height: '4px',
                      background: TOOL_COLORS.OTHER.primary,
                      borderRadius: '2px',
                      transform: 'translateX(-50%)',
                      transition: 'width 0.4s ease-in-out',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 10' preserveAspectRatio='none'%3E%3Cpath d='M0 5 Q25 0 50 5 T100 5' fill='none' stroke='%23FF8C00' stroke-width='4'/%3E%3C/svg%3E")`,
                      backgroundSize: '100% 100%',
                    },
                    '&:hover::after': {
                      width: '90%',
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => {
                      setSelectedTool(item.path);
                      navigate(item.path);
                    }}
                    sx={{ p: 2.5, height: '100%' }}
                  >
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* Icon Section */}
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        bgcolor: `${TOOL_COLORS.OTHER.primary}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <IconComponent size={24} color={TOOL_COLORS.OTHER.primary} />
                    </Box>

                    {/* Content Section */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ 
                            fontWeight: 600,
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {item.name}
                          </Typography>
                          {item.isNew && (
                            <Chip
                              label="NEW"
                              size="small"
                              sx={{
                                bgcolor: 'error.main',
                                color: 'white',
                                height: 20,
                                fontSize: '0.6rem',
                                fontWeight: 700,
                                ml: 1,
                                flexShrink: 0
                              }}
                            />
                          )}
                        </Box>
                        <Tooltip title={isFav ? 'Remove from favorites' : 'Add to favorites'}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite({ text: item.name, path: item.path, category: 'others' });
                            }}
                            sx={{ 
                              ml: 1,
                              color: isFav ? '#FFD700' : theme.palette.action.disabled,
                            }}
                          >
                            {isFav ? '★' : '☆'}
                          </IconButton>
                        </Tooltip>
                      </Box>
                      
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          mb: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {item.description}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FaClock size={12} color={theme.palette.text.secondary} />
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          {getLastUsed(item.path)}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Chip
                          label={item.category}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: TOOL_COLORS.OTHER.light,
                            color: TOOL_COLORS.OTHER.primary,
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                  </CardActionArea>
                </Paper>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OthersLanding;